import { gql } from '@apollo/client';
import { useSubscription } from '@apollo/client/react';

import { Food, FoodComponent, Kitchen } from '../libs';
import { SUBSCRIBE_TO_UPDATE_FOOD_COMPONENT_STATUS_SUBSCRIPTION } from '../libs/appGraphQL';
import { FOOD_COMPONENT_FIELDS } from '../libs/appGraphQL/fragments';
import {
  SUBSCRIBE_TO_BATCH_CREATE_COMPONENT_SHORTAGE_SUBSCRIPTION,
  SUBSCRIBE_TO_BATCH_UPDATE_FOODS_PORTIONING_STATUS_SUBSCRIPTION,
  SUBSCRIBE_TO_UPDATE_FOOD_PORTIONING_SUBSCRIPTION
} from '../libs/graphQL';
import { COMPONENT_SHORTAGE_FIELDS, FOOD_FIELDS } from '../libs/graphQL/fragments';
import { costFreeFragmentName, withoutCostFields } from '../libs/graphQL/withoutCosts';

// The messages carry no cost, so the cache is written with the fragments that name none.
const FOOD_COMPONENT_LIVE_FIELDS = withoutCostFields(FOOD_COMPONENT_FIELDS);
const FOOD_LIVE_FIELDS = withoutCostFields(FOOD_FIELDS);

const COMPONENT_SHORTAGES_FRAGMENT = gql`
  ${COMPONENT_SHORTAGE_FIELDS}
  fragment ComponentShortagesUpdate on FoodComponent {
    shortages {
      ...ComponentShortageFields
    }
  }
`;

// ignoreResults: a message must not re-render the host. no-cache: Apollo would otherwise write every kitchen's
// payload into the cache before onData runs, so onData is the only writer and keeps this kitchen's records.
const LIVE_UPDATE_OPTIONS = { fetchPolicy: 'no-cache', ignoreResults: true } as const;

interface FoodComponentStatusData {
  subscribeToUpdateFoodComponentStatus?: FoodComponent | null;
}

interface ComponentShortagesData {
  subscribeToBatchCreateComponentShortage?: (FoodComponent | null)[] | null;
}

interface FoodPortioningData {
  subscribeToUpdateFoodPortioning?: Food | null;
}

interface FoodsPortioningData {
  subscribeToBatchUpdateFoodsPortioningStatus?: (Food | null)[] | null;
}

/** Applies live food and component updates for `kitchen` to the Apollo cache, without re-rendering the caller. */
const useKitchenLiveUpdates = (kitchen: Kitchen) => {
  useSubscription<FoodComponentStatusData>(SUBSCRIBE_TO_UPDATE_FOOD_COMPONENT_STATUS_SUBSCRIPTION, {
    ...LIVE_UPDATE_OPTIONS,
    variables: { kitchen },
    onData: ({ data, client }) => {
      const component = data.data?.subscribeToUpdateFoodComponentStatus;
      if (!component || component.kitchen !== kitchen) return;
      client.cache.writeFragment({
        id: client.cache.identify(component),
        fragment: FOOD_COMPONENT_LIVE_FIELDS,
        fragmentName: costFreeFragmentName('FoodComponentFields'),
        data: component
      });
    }
  });

  useSubscription<ComponentShortagesData>(SUBSCRIBE_TO_BATCH_CREATE_COMPONENT_SHORTAGE_SUBSCRIPTION, {
    ...LIVE_UPDATE_OPTIONS,
    onData: ({ data, client }) => {
      const components = (data.data?.subscribeToBatchCreateComponentShortage ?? []).filter(
        (component: FoodComponent | null): component is FoodComponent => component?.kitchen === kitchen
      );
      if (components.length === 0) return;
      client.cache.batch({
        update: (cache) => {
          for (const component of components) {
            cache.writeFragment({
              id: cache.identify(component),
              fragment: COMPONENT_SHORTAGES_FRAGMENT,
              fragmentName: 'ComponentShortagesUpdate',
              data: { shortages: component.shortages }
            });
          }
        }
      });
    }
  });

  useSubscription<FoodPortioningData>(SUBSCRIBE_TO_UPDATE_FOOD_PORTIONING_SUBSCRIPTION, {
    ...LIVE_UPDATE_OPTIONS,
    variables: { kitchen },
    onData: ({ data, client }) => {
      const food = data.data?.subscribeToUpdateFoodPortioning;
      if (!food || food.kitchen !== kitchen) return;
      client.cache.writeFragment({
        id: client.cache.identify(food),
        fragment: FOOD_LIVE_FIELDS,
        fragmentName: costFreeFragmentName('FoodFields'),
        data: food
      });
    }
  });

  useSubscription<FoodsPortioningData>(SUBSCRIBE_TO_BATCH_UPDATE_FOODS_PORTIONING_STATUS_SUBSCRIPTION, {
    ...LIVE_UPDATE_OPTIONS,
    onData: ({ data, client }) => {
      const foods = (data.data?.subscribeToBatchUpdateFoodsPortioningStatus ?? []).filter(
        (food: Food | null): food is Food => food?.kitchen === kitchen
      );
      if (foods.length === 0) return;
      client.cache.batch({
        update: (cache) => {
          for (const food of foods) {
            cache.writeFragment({
              id: cache.identify(food),
              fragment: FOOD_LIVE_FIELDS,
              fragmentName: costFreeFragmentName('FoodFields'),
              data: food
            });
          }
        }
      });
    }
  });
};

export default useKitchenLiveUpdates;
